import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import APIDASH from "../../../api/apidash";
import apisecured from "../../../api/apisecured";
import { setAccountUpdateData } from "../../../State/authSlicer";
import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges.js";
import { setToast } from "../../../State/toastSlice.js";

// ─── CSS ────────────────────────────────────────────────────────────────────
const SPONSA_CSS = `
  :root {
    --bg-primary: #030021;
    --bg-card: rgba(255,255,255,0.03);
    --bg-card-hover: rgba(255,255,255,0.05);
    --bg-elevated: rgba(255,255,255,0.06);
    --bg-input: rgba(255,255,255,0.04);
    --bg-input-focus: rgba(255,255,255,0.08);
    --primary: #00FF8F;
    --primary-dim: rgba(0,255,143,0.1);
    --primary-glow: rgba(0,255,143,0.3);
    --yellow: #fbbf24;
    --yellow-dim: rgba(251,191,36,0.1);
    --coral: #f472b6;
    --coral-dim: rgba(244,114,182,0.1);
    --purple: #a855f7;
    --purple-dim: rgba(168,85,247,0.1);
    --danger: #ef4444;
    --danger-dim: rgba(239,68,68,0.1);
    --text-primary: #ffffff;
    --text-secondary: rgba(255,255,255,0.6);
    --text-tertiary: rgba(255,255,255,0.4);
    --border: rgba(255,255,255,0.08);
    --border-hover: rgba(255,255,255,0.15);
  }

  .sp-settings-wrap {
    background: var(--bg-primary);
    min-height: 100vh;
    color: var(--text-primary);
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    line-height: 1.5;
    position: relative;
  }

  .sp-settings-wrap::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse at 30% 20%, rgba(0,255,143,0.04) 0%, transparent 50%),
      radial-gradient(ellipse at 70% 80%, rgba(168,85,247,0.03) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  .sp-settings-inner {
    position: relative;
    z-index: 1;
    padding: 32px 48px 40px;
    max-width: 700px;
    margin: 0 auto;
  }

  /* PAGE HEADER */
  .page-header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 32px;
  }
  .page-header { margin-bottom: 0; }
  .page-title {
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  .page-description { font-size: 15px; color: var(--text-secondary); margin: 0; }
  .view-page-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 10px;
    background: var(--bg-card);
    border: 1px solid var(--border);
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    text-decoration: none;
    flex-shrink: 0;
    margin-top: 4px;
  }
  .view-page-btn:hover {
    background: var(--primary-dim);
    border-color: var(--primary);
    color: var(--primary);
  }
  .view-page-btn svg { width: 15px; height: 15px; }

  /* SECTIONS */
  .settings-section {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 28px;
    margin-bottom: 24px;
  }
  .section-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border);
  }
  .section-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .section-icon.green { background: var(--primary-dim); color: var(--primary); }
  .section-icon.yellow { background: var(--yellow-dim); color: var(--yellow); }
  .section-icon.coral { background: var(--coral-dim); color: var(--coral); }
  .section-icon svg { width: 20px; height: 20px; }
  .section-title-group { flex: 1; }
  .section-title { font-size: 17px; font-weight: 600; color: var(--text-primary); margin: 0 0 2px 0; }
  .section-subtitle { font-size: 13px; color: var(--text-tertiary); margin: 0; }

  /* FORM */
  .form-row { margin-bottom: 20px; }
  .form-row:last-child { margin-bottom: 0; }
  .form-row.two-column { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
  }
  .form-label-hint { font-weight: 400; color: var(--text-tertiary); margin-left: 4px; }
  .form-input {
    width: 100%;
    background: var(--bg-input);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 12px 16px;
    font-family: inherit;
    font-size: 14px;
    color: var(--text-primary);
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    box-sizing: border-box;
  }
  .form-input:hover { border-color: var(--border-hover); }
  .form-input:focus {
    outline: none;
    background: var(--bg-input-focus);
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-glow);
  }
  .form-input::placeholder { color: var(--text-tertiary); }
  textarea.form-input { resize: vertical; min-height: 100px; }

  .input-with-prefix { display: flex; align-items: stretch; }
  .input-prefix {
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border);
    border-right: none;
    border-radius: 10px 0 0 10px;
    padding: 12px 14px;
    font-size: 14px;
    color: var(--text-tertiary);
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  .input-with-prefix .form-input { border-radius: 0 10px 10px 0; }

  /* BANNER */
  .banner-upload { width: 100%; position: relative; }
  .banner-preview {
    width: 100%; height: 140px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(0,255,143,0.1) 0%, rgba(244,114,182,0.1) 100%);
    border: 2px dashed var(--border);
    display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
    overflow: hidden;
    position: relative;
    box-sizing: border-box;
  }
  .banner-preview:hover { border-color: var(--primary); }
  .banner-preview.has-image { border-style: solid; border-color: var(--primary); }
  .banner-preview img { width: 100%; height: 100%; object-fit: cover; position: absolute; top:0; left:0; }
  .banner-preview.drag-over {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-glow);
  }
  .upload-content { display: flex; flex-direction: column; align-items: center; gap: 8px; position: relative; z-index: 1; }
  .upload-text { color: var(--text-secondary); font-size: 14px; text-align: center; }
  .banner-remove-btn {
    position: absolute; top: 8px; right: 8px;
    width: 32px; height: 32px;
    border-radius: 8px;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(4px);
    border: none; color: white;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s ease;
    z-index: 2;
  }
  .banner-remove-btn:hover { background: var(--danger); }

  /* PROFILE CIRCLE */
  .profile-upload-container { display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .profile-circle {
    width: 120px; height: 120px;
    border-radius: 50%;
    background: rgba(255,255,255,0.05);
    border: 3px dashed rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    overflow: hidden;
    position: relative;
  }
  .profile-circle:hover { border-color: var(--primary); background: rgba(0,255,143,0.05); }
  .profile-circle.has-image { border-style: solid; border-color: var(--primary); }
  .profile-circle.drag-over { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-glow); }
  .profile-circle img { width: 100%; height: 100%; object-fit: cover; }
  .profile-placeholder { text-align: center; }
  .profile-placeholder-icon { font-size: 32px; margin-bottom: 4px; }
  .profile-placeholder-text { font-size: 12px; color: var(--text-tertiary); }
  .profile-actions { display: flex; flex-direction: column; align-items: center; gap: 8px; }
  .profile-requirements { font-size: 12px; color: var(--text-tertiary); text-align: center; margin: 0; }
  .remove-photo-btn {
    font-size: 13px; color: var(--danger);
    background: none; border: none;
    cursor: pointer; padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.15s ease;
    font-family: inherit;
  }
  .remove-photo-btn:hover { background: var(--danger-dim); }

  /* SOCIAL LINKS */
  .social-links-list { display: flex; flex-direction: column; gap: 12px; }
  .social-link-row { display: flex; align-items: center; gap: 12px; }
  .social-favicon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: var(--bg-input);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; overflow: hidden;
  }
  .social-favicon img { width: 24px; height: 24px; }
  .social-favicon svg { width: 20px; height: 20px; color: var(--text-tertiary); }
  .social-link-row .form-input { flex: 1; }
  .remove-link-btn {
    width: 36px; height: 36px;
    border-radius: 8px;
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-tertiary);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s ease;
    flex-shrink: 0;
  }
  .remove-link-btn:hover { background: var(--danger-dim); border-color: var(--danger); color: var(--danger); }
  .add-link-btn {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px;
    border: 2px dashed var(--border);
    border-radius: 10px;
    background: transparent;
    color: var(--text-secondary);
    font-family: inherit; font-size: 14px; font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    width: 100%;
    box-sizing: border-box;
  }
  .add-link-btn:hover { border-color: var(--primary); color: var(--primary); background: var(--primary-dim); }

  /* TAGS */
  .tags-grid { display: flex; flex-wrap: wrap; gap: 10px; }
  .tag-chip {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 14px;
    background: var(--bg-input);
    border: 1px solid var(--border);
    border-radius: 100px;
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
  }
  .tag-chip:hover { border-color: var(--border-hover); background: var(--bg-input-focus); }
  .tag-chip.selected { background: var(--primary-dim); border-color: var(--primary); }
  .tag-chip.disabled { opacity: 0.4; cursor: not-allowed; }
  .tag-chip.disabled:hover { border-color: var(--border); background: var(--bg-input); }
  .tag-icon { font-size: 16px; line-height: 1; }
  .tag-label { font-size: 13px; font-weight: 500; color: var(--text-secondary); transition: color 0.15s ease; }
  .tag-chip.selected .tag-label { color: var(--primary); }
  .tags-counter { margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border); }
  .tags-count { font-size: 13px; color: var(--text-tertiary); }
  .tags-count span { font-weight: 600; color: var(--primary); }

  /* SAVE BAR */
  .save-bar {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    background: rgba(3,0,33,0.95);
    backdrop-filter: blur(12px);
    border-top: 1px solid var(--border);
    padding: 16px 48px;
    display: flex; align-items: center; justify-content: center;
    gap: 12px;
    transform: translateY(100%);
    transition: transform 0.3s ease;
    z-index: 100;
  }
  .save-bar.visible { transform: translateY(0); }
  .save-bar-inner {
    width: 100%; max-width: 700px;
    display: flex; align-items: center; justify-content: flex-end;
    gap: 12px;
  }
  .save-bar-text { font-size: 14px; color: var(--text-secondary); margin-right: auto; }

  /* BUTTONS */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 10px 18px;
    border-radius: 10px;
    font-family: inherit; font-size: 14px; font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;
  }
  .btn-primary { background: var(--primary); color: #030021; }
  .btn-primary:hover { background: #00e680; box-shadow: 0 0 20px rgba(0,255,143,0.4); }
  .btn-secondary {
    background: var(--bg-card);
    color: var(--text-primary);
    border: 1px solid var(--border);
  }
  .btn-secondary:hover { background: var(--bg-card-hover); border-color: var(--border-hover); }

  /* CONFIRM MODAL */
  .confirm-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(3,0,33,0.85);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000;
    opacity: 0; visibility: hidden;
    transition: all 0.2s ease;
  }
  .confirm-modal-overlay.visible { opacity: 1; visibility: visible; }
  .confirm-modal {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 24px;
    max-width: 400px; width: 90%;
    text-align: center;
    transform: scale(0.95);
    transition: transform 0.2s ease;
  }
  .confirm-modal-overlay.visible .confirm-modal { transform: scale(1); }
  .confirm-modal-title { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0 0 8px 0; }
  .confirm-modal-message { font-size: 14px; color: var(--text-secondary); margin: 0 0 24px 0; line-height: 1.5; }
  .confirm-modal-buttons { display: flex; gap: 12px; justify-content: center; }
  .confirm-modal-buttons .btn { flex: 1; max-width: 140px; }

  /* FORM HELP */
  .form-help { font-size: 12px; color: var(--text-tertiary); margin-top: 6px; }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .sp-settings-inner { padding: 24px 16px; }
    .save-bar { padding: 16px 20px; }
    .form-row.two-column { grid-template-columns: 1fr; }
    .page-title { font-size: 24px; }
    .page-header-row { flex-direction: column; gap: 12px; }
    .view-page-btn { align-self: flex-start; }
    .settings-section { padding: 20px 16px; }
    .banner-preview .upload-text { font-size: 12px; line-height: 1.4; }
  }
`;

// ─── CONSTANTS ──────────────────────────────────────────────────────────────
const ALL_TAGS = [
  { id: "vtuber", icon: "🎭", label: "VTuber" },
  { id: "gaming", icon: "🎮", label: "Gaming" },
  { id: "react", icon: "📺", label: "React/Commentary" },
  { id: "asmr", icon: "🎧", label: "ASMR" },
  { id: "music", icon: "🎵", label: "Music" },
  { id: "art", icon: "🎨", label: "Art" },
  { id: "cosplay", icon: "✨", label: "Cosplay" },
  { id: "justchatting", icon: "💬", label: "Just Chatting" },
  { id: "irl", icon: "📷", label: "IRL Creator" },
  { id: "educational", icon: "📚", label: "Educational" },
  { id: "podcaster", icon: "🎙️", label: "Podcaster" },
  { id: "cooking", icon: "🍳", label: "Cooking" },
  { id: "fitness", icon: "💪", label: "Fitness" },
];

const TAG_ID_MAP = {
  asmr: 1,
  cosplay: 2,
  irl: 3,
  music: 4,
  vtuber: 5,
  gaming: 6,
  react: 7,
  art: 8,
  justchatting: 9,
  educational: 10,
  podcaster: 11,
  cooking: 12,
  fitness: 13,
};

const DEFAULTS = {
  displayName: "",
  username: "",
  about: "",
  socialLinks: [],
  selectedTags: new Set(),
  bannerSrc: "",
  profileSrc: "",
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function getFavicon(url) {
  try {
    const { hostname } = new URL(
      url.startsWith("http") ? url : `https://${url}`,
    );
    return hostname
      ? `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`
      : null;
  } catch {
    return null;
  }
}

function buildImageUrl(path) {
  if (!path) return "";
  if (typeof path !== "string") return "";
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:") ||
    path.startsWith("blob:")
  ) {
    return path;
  }
  return `${APIDASH}${path}`;
}

function normalizeLinks(links) {
  return (links || []).map((x) => (x || "").trim()).filter(Boolean);
}

function normalizeTagSet(setOrArray) {
  return [
    ...(setOrArray instanceof Set ? setOrArray : new Set(setOrArray || [])),
  ].sort();
}

function arraysEqual(a, b) {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

// ─── SAVE BAR ────────────────────────────────────────────────────────────────
function SaveBar({ visible, onDiscard, onSave, saving }) {
  return (
    <div className={`save-bar${visible ? " visible" : ""}`}>
      <div className="save-bar-inner">
        <span className="save-bar-text">You have unsaved changes</span>
        <button
          className="btn btn-secondary"
          onClick={onDiscard}
          disabled={saving}
          type="button"
        >
          Discard
        </button>
        <button
          className="btn btn-primary"
          onClick={onSave}
          disabled={saving}
          type="button"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

// ─── CONFIRM MODAL ───────────────────────────────────────────────────────────
function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Leave page",
  cancelText = "Stay here",
}) {
  if (!open) return null;
  return (
    <div
      className={`confirm-modal-overlay${open ? " visible" : ""}`}
      onClick={onClose}
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            width: 48,
            height: 48,
            background: "rgba(251,191,36,0.1)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            color: "#fbbf24",
          }}
        >
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h3 className="confirm-modal-title">{title}</h3>
        <p className="confirm-modal-message">{description}</p>
        <div className="confirm-modal-buttons">
          <button className="btn btn-secondary" onClick={onClose} type="button">
            {cancelText}
          </button>
          <button className="btn btn-primary" onClick={onConfirm} type="button">
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function ProfileSettingPage() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();

  const bannerInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const aboutRef = useRef(null);

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  const [bannerDragOver, setBannerDragOver] = useState(false);
  const [profileDragOver, setProfileDragOver] = useState(false);

  const [displayName, setDisplayName] = useState(DEFAULTS.displayName);
  const [username, setUsername] = useState(DEFAULTS.username);
  const [about, setAbout] = useState(DEFAULTS.about);
  const [socialLinks, setSocialLinks] = useState(DEFAULTS.socialLinks);
  const [selectedTags, setSelectedTags] = useState(DEFAULTS.selectedTags);
  const [bannerSrc, setBannerSrc] = useState(DEFAULTS.bannerSrc);
  const [profileSrc, setProfileSrc] = useState(DEFAULTS.profileSrc);

  const [profileFile, setProfileFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const [usernameInitial, setUsernameInitial] = useState("");
  const [usernameValid, setUsernameValid] = useState(true);
  const [removeBannerImage, setRemoveBannerImage] = useState(false);
  const [removeProfileImage, setRemoveProfileImage] = useState(false);

  const [initialState, setInitialState] = useState({
    displayName: "",
    username: "",
    about: "",
    socialLinks: [],
    selectedTags: [],
    bannerSrc: "",
    profileSrc: "",
  });

  const normalizeSocialLinksFromAuth = (links) => {
    if (!Array.isArray(links) || links.length === 0) return [];
    return links.map((link) => {
      if (typeof link === "string") return link;
      return link?.url || "";
    });
  };

  const deriveSelectedTagsFromAuth = useCallback((authState) => {
    if (
      Array.isArray(authState?.selectedTags) &&
      authState.selectedTags.every((tag) => typeof tag === "string")
    ) {
      return new Set(authState.selectedTags);
    }

    if (Array.isArray(authState?.selectedTags)) {
      const reverseMap = Object.entries(TAG_ID_MAP).reduce(
        (acc, [key, value]) => {
          if (value) acc[value] = key;
          return acc;
        },
        {},
      );

      const mapped = authState.selectedTags
        .map((id) => reverseMap[id])
        .filter(Boolean);

      return new Set(mapped);
    }

    return new Set();
  }, []);

  useEffect(() => {
    const authDisplayName = auth?.displayName || "";
    const authUsername = auth?.sponsaUserName || "";
    const authAbout = auth?.bio || "";
    const authSocialLinks = normalizeSocialLinksFromAuth(auth?.socialLinks);
    const authSelectedTags = deriveSelectedTagsFromAuth(auth);
    const authBanner = buildImageUrl(auth?.CoverIMG);
    const authProfile = buildImageUrl(auth?.userImg);

    setDisplayName(authDisplayName);
    setUsername(authUsername);
    setAbout(authAbout);
    setSocialLinks(authSocialLinks);
    setSelectedTags(authSelectedTags);
    setBannerSrc(authBanner);
    setProfileSrc(authProfile);
    setUsernameInitial(authUsername);
    setUsernameValid(true);

    setInitialState({
      displayName: authDisplayName,
      username: authUsername,
      about: authAbout,
      socialLinks: authSocialLinks,
      selectedTags: [...authSelectedTags],
      bannerSrc: authBanner,
      profileSrc: authProfile,
    });

    setProfileFile(null);
    setBannerFile(null);
    setRemoveBannerImage(false);
    setRemoveProfileImage(false);
    setDirty(false);
  }, [auth, deriveSelectedTagsFromAuth]);

  useEffect(() => {
    if (aboutRef.current) {
      aboutRef.current.style.height = "auto";
      aboutRef.current.style.height =
        Math.max(100, aboutRef.current.scrollHeight) + "px";
    }
  }, [about]);

  const currentComparableState = useMemo(() => {
    return {
      displayName: (displayName || "").trim(),
      username: (username || "").trim(),
      about: about || "",
      socialLinks: normalizeLinks(socialLinks),
      selectedTags: normalizeTagSet(selectedTags),
      bannerSrc: bannerSrc || "",
      profileSrc: profileSrc || "",
      removeBannerImage,
      removeProfileImage,
      hasBannerFile: !!bannerFile,
      hasProfileFile: !!profileFile,
    };
  }, [
    displayName,
    username,
    about,
    socialLinks,
    selectedTags,
    bannerSrc,
    profileSrc,
    removeBannerImage,
    removeProfileImage,
    bannerFile,
    profileFile,
  ]);

  const initialComparableState = useMemo(() => {
    return {
      displayName: (initialState.displayName || "").trim(),
      username: (initialState.username || "").trim(),
      about: initialState.about || "",
      socialLinks: normalizeLinks(initialState.socialLinks),
      selectedTags: normalizeTagSet(initialState.selectedTags),
      bannerSrc: initialState.bannerSrc || "",
      profileSrc: initialState.profileSrc || "",
      removeBannerImage: false,
      removeProfileImage: false,
      hasBannerFile: false,
      hasProfileFile: false,
    };
  }, [initialState]);

  useEffect(() => {
    const isDirty =
      currentComparableState.displayName !==
        initialComparableState.displayName ||
      currentComparableState.username !== initialComparableState.username ||
      currentComparableState.about !== initialComparableState.about ||
      currentComparableState.bannerSrc !== initialComparableState.bannerSrc ||
      currentComparableState.profileSrc !== initialComparableState.profileSrc ||
      currentComparableState.removeBannerImage !==
        initialComparableState.removeBannerImage ||
      currentComparableState.removeProfileImage !==
        initialComparableState.removeProfileImage ||
      currentComparableState.hasBannerFile !==
        initialComparableState.hasBannerFile ||
      currentComparableState.hasProfileFile !==
        initialComparableState.hasProfileFile ||
      !arraysEqual(
        currentComparableState.socialLinks,
        initialComparableState.socialLinks,
      ) ||
      !arraysEqual(
        currentComparableState.selectedTags,
        initialComparableState.selectedTags,
      );

    setDirty(isDirty);
  }, [currentComparableState, initialComparableState]);

  useUnsavedChanges(dirty);

  const checkUsernameExists = async (nextUsername) => {
    try {
      const response = await apisecured.get("account/CheckUsername", {
        params: {
          username: nextUsername,
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Username check error:", error);

      return {
        success: false,
        data: error.response?.data || error.message,
      };
    }
  };

  const checkUsernameAvailability = useCallback(
    debounce(async (nextUsername) => {
      const value = nextUsername?.trim();

      if (!value) {
        setUsernameValid(true);
        return;
      }

      if (value === usernameInitial) {
        setUsernameValid(true);
        return;
      }

      const result = await checkUsernameExists(value);

      if (result.success) {
        setUsernameValid(result.data?.available === true);
      } else {
        setUsernameValid(false);
      }
    }, 500),
    [usernameInitial],
  );

  useEffect(() => {
    checkUsernameAvailability(username);

    return () => {
      checkUsernameAvailability.cancel?.();
    };
  }, [username, checkUsernameAvailability]);

  const handleAbout = (e) => {
    setAbout(e.target.value);
  };

  const addLink = () => {
    setSocialLinks((prev) => [...prev, ""]);
  };

  const removeLink = (index) => {
    setSocialLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLink = (index, value) => {
    setSocialLinks((prev) =>
      prev.map((item, i) => (i === index ? value : item)),
    );
  };

  const toggleTag = (id) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 5) next.add(id);
      return next;
    });
  };

  const handleBannerFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;

    const fileUrl = URL.createObjectURL(file);
    setBannerFile(file);
    setBannerSrc(fileUrl);
    setRemoveBannerImage(false);
  };

  const handleProfileFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;

    const fileUrl = URL.createObjectURL(file);
    setProfileFile(file);
    setProfileSrc(fileUrl);
    setRemoveProfileImage(false);
  };

  const handleBannerDrop = async (e) => {
    e.preventDefault();
    setBannerDragOver(false);
    const file = e.dataTransfer.files?.[0];
    await handleBannerFile(file);
  };

  const handleProfileDrop = async (e) => {
    e.preventDefault();
    setProfileDragOver(false);
    const file = e.dataTransfer.files?.[0];
    await handleProfileFile(file);
  };

  const discardChanges = useCallback(() => {
    setDisplayName(initialState.displayName);
    setUsername(initialState.username);
    setAbout(initialState.about);
    setSocialLinks(initialState.socialLinks);
    setSelectedTags(new Set(initialState.selectedTags));
    setBannerSrc(initialState.bannerSrc);
    setProfileSrc(initialState.profileSrc);
    setProfileFile(null);
    setBannerFile(null);
    setRemoveBannerImage(false);
    setRemoveProfileImage(false);
    setUsernameValid(true);

    if (aboutRef.current) {
      aboutRef.current.style.height = "auto";
    }
  }, [initialState]);

  useEffect(() => {
    const handler = () => {
      discardChanges();
    };

    window.addEventListener("sp-force-discard-unsaved-changes", handler);
    return () =>
      window.removeEventListener("sp-force-discard-unsaved-changes", handler);
  }, [discardChanges]);
  const handleSave = async () => {
    if (usernameValid === false) {
      dispatch(
        setToast({
          message: "Username already exists. Please choose a different one.",
          type: "error",
        }),
      );
      return;
    }

    setSaving(true);

    try {
      const formdata = new FormData();
      formdata.append("SponsaUserName", username.trim());
      formdata.append("DisplayName", displayName);
      formdata.append("Bio", about);
      formdata.append("FbUrl", auth?.FbUrl || "");
      formdata.append("InstaUrl", auth?.InstaUrl || "");
      formdata.append("TwitterUrl", auth?.TwitterUrl || "");
      formdata.append("YtUrl", auth?.YtUrl || "");
      formdata.append("IsFromFullProfileForm", "true");

      if (profileFile) {
        formdata.append("Image", profileFile);
      }

      if (bannerFile) {
        formdata.append("CoverImage", bannerFile);
      }

      if (removeProfileImage) {
        formdata.append("RemoveImage", "true");
      }

      if (removeBannerImage) {
        formdata.append("RemoveCoverImage", "true");
      }

      socialLinks.forEach((link, i) => {
        if (link?.trim() !== "") {
          formdata.append(`SocialLinks[${i}].Url`, link.trim());
        }
      });

      [...selectedTags].forEach((tagKey, i) => {
        const mappedId = TAG_ID_MAP[tagKey];
        if (mappedId) {
          formdata.append(`SelectedTagIds[${i}]`, mappedId);
        }
      });

      const response = await apisecured.post("account/UpdateAccount", formdata);

      if (response.status === 200) {
        const result = response.data || {};

        const nextProfileSrc = removeProfileImage
          ? ""
          : result.imageURL
            ? buildImageUrl(result.imageURL)
            : profileSrc;

        const nextBannerSrc = removeBannerImage
          ? ""
          : result.coverImageUrl
            ? buildImageUrl(result.coverImageUrl)
            : bannerSrc;

        const cleanedSocialLinks = socialLinks.filter(
          (link) => link.trim() !== "",
        );

        dispatch(
          setAccountUpdateData({
            sponsaUserName: username.trim(),
            displayName,
            userImg: removeProfileImage ? "" : result.imageURL || auth?.userImg,
            CoverIMG: removeBannerImage
              ? ""
              : result.coverImageUrl || auth?.CoverIMG,
            bio: about,
            YtUrl: auth?.YtUrl || "",
            TwitterUrl: auth?.TwitterUrl || "",
            InstaUrl: auth?.InstaUrl || "",
            FbUrl: auth?.FbUrl || "",
            socialLinks: cleanedSocialLinks.map((url) => ({ url })),
            selectedTags: [...selectedTags],
          }),
        );

        setInitialState({
          displayName,
          username: username.trim(),
          about,
          socialLinks: cleanedSocialLinks,
          selectedTags: [...selectedTags],
          bannerSrc: nextBannerSrc,
          profileSrc: nextProfileSrc,
        });

        setBannerSrc(nextBannerSrc);
        setProfileSrc(nextProfileSrc);
        setProfileFile(null);
        setBannerFile(null);
        setRemoveProfileImage(false);
        setRemoveBannerImage(false);
        setDirty(false);

        dispatch(
          setToast({
            message: "Profile updated successfully.",
            type: "success",
          }),
        );
      }
    } catch (error) {
      console.error("Error updating account:", error);
      dispatch(
        setToast({
          message: "An error occurred while updating account details",
          type: "error",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAttemptNavigate = (path) => {
    if (!dirty) {
      navigate(path);
      return;
    }

    if (path === location.pathname) return;

    setPendingPath(path);
    setShowDiscardModal(true);
  };

  const handleStayOnPage = () => {
    setPendingPath(null);
    setShowDiscardModal(false);
  };

  const handleLeavePage = () => {
    setShowDiscardModal(false);
    discardChanges();

    if (pendingPath) {
      navigate(pendingPath);
      setPendingPath(null);
    }
  };

  return (
    <>
      <style>{SPONSA_CSS}</style>

      <div className="sp-settings-wrap">
        <div className="sp-settings-inner">
          {/* ── HEADER ── */}
          <div className="page-header-row">
            <header className="page-header">
              <h1 className="page-title">Profile Settings</h1>
              <p className="page-description">
                Manage how you appear to your supporters and customise your
                SPONSA page.
              </p>
            </header>

            <a
              href={
                username
                  ? `https://sponsa.app/${username}`
                  : "https://sponsa.app/"
              }
              target="_blank"
              rel="noopener noreferrer"
              className="view-page-btn"
            >
              <svg
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              View my page
            </a>
          </div>

          {/* Example usage for route navigation with confirmation */}
          {/* 
          <button type="button" onClick={() => handleAttemptNavigate("/dashboard")}>
            Go to dashboard
          </button>
          */}

          {/* ── PROFILE MEDIA ── */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon green">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">Profile Media</h2>
                <p className="section-subtitle">
                  Your banner image and profile picture
                </p>
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">Banner Image</label>
              <div className="banner-upload">
                <div
                  className={`banner-preview${bannerSrc ? " has-image" : ""}${bannerDragOver ? " drag-over" : ""}`}
                  onClick={() => bannerInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setBannerDragOver(true);
                  }}
                  onDragLeave={() => setBannerDragOver(false)}
                  onDrop={handleBannerDrop}
                >
                  {bannerSrc ? (
                    <img src={bannerSrc} alt="Banner" />
                  ) : (
                    <div className="upload-content">
                      <svg
                        width="32"
                        height="32"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                        style={{ color: "rgba(255,255,255,0.4)" }}
                      >
                        <rect
                          x="3"
                          y="3"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span className="upload-text">
                        Click or drag to upload banner (1200×400px recommended)
                      </span>
                    </div>
                  )}

                  {bannerSrc && (
                    <button
                      type="button"
                      className="banner-remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setBannerSrc("");
                        setBannerFile(null);
                        setRemoveBannerImage(true);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>

                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    await handleBannerFile(file);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>

            <div className="form-row" style={{ marginTop: 24 }}>
              <label className="form-label">Profile Picture</label>
              <div className="profile-upload-container">
                <div
                  className={`profile-circle${profileSrc ? " has-image" : ""}${profileDragOver ? " drag-over" : ""}`}
                  onClick={() => profileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setProfileDragOver(true);
                  }}
                  onDragLeave={() => setProfileDragOver(false)}
                  onDrop={handleProfileDrop}
                >
                  {profileSrc ? (
                    <img src={profileSrc} alt="Profile" />
                  ) : (
                    <div className="profile-placeholder">
                      <div className="profile-placeholder-icon">📷</div>
                      <div className="profile-placeholder-text">
                        Click or drag to upload
                      </div>
                    </div>
                  )}
                </div>

                <input
                  ref={profileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    await handleProfileFile(file);
                    e.target.value = "";
                  }}
                />

                <div className="profile-actions">
                  {profileSrc && (
                    <button
                      type="button"
                      className="remove-photo-btn"
                      onClick={() => {
                        setProfileSrc("");
                        setProfileFile(null);
                        setRemoveProfileImage(true);
                      }}
                    >
                      Remove photo
                    </button>
                  )}
                  <p className="profile-requirements">
                    PNG or JPG. Max 2MB. Recommended 400×400px.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── BASIC INFO ── */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon yellow">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">Basic Information</h2>
                <p className="section-subtitle">Your public profile details</p>
              </div>
            </div>

            <div className="form-row two-column">
              <div>
                <label className="form-label">Display Name</label>
                <input
                  className="form-input"
                  type="text"
                  value={displayName}
                  placeholder="Your display name"
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                  }}
                />
              </div>

              <div>
                <label className="form-label">Username</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">sponsa.app/</span>
                  <input
                    className="form-input"
                    type="text"
                    value={username}
                    placeholder="username"
                    onChange={(e) => {
                      setUsername(e.target.value);
                    }}
                  />
                </div>

                {username !== usernameInitial && usernameValid === false && (
                  <div style={{ color: "#ef4444", marginTop: 8, fontSize: 13 }}>
                    Username already exists
                  </div>
                )}

                {username !== usernameInitial &&
                  usernameValid === true &&
                  username.trim() && (
                    <div
                      style={{ color: "#22c55e", marginTop: 8, fontSize: 13 }}
                    >
                      Username is available
                    </div>
                  )}
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                About <span className="form-label-hint">(optional)</span>
              </label>
              <textarea
                ref={aboutRef}
                className="form-input"
                value={about}
                placeholder="Tell your supporters what you create..."
                style={{ overflow: "hidden", resize: "vertical" }}
                onChange={handleAbout}
              />
            </div>
          </section>

          {/* ── SOCIAL LINKS ── */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon coral">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">Social Links</h2>
                <p className="section-subtitle">Connect your social profiles</p>
              </div>
            </div>

            <div className="social-links-list">
              {socialLinks.map((url, i) => {
                const favicon = url ? getFavicon(url) : null;

                return (
                  <div key={i} className="social-link-row">
                    <div className="social-favicon">
                      {favicon ? (
                        <img
                          src={favicon}
                          alt=""
                          onError={(e) => {
                            e.target.style.display = "none";
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = "block";
                            }
                          }}
                        />
                      ) : null}

                      <svg
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        style={{ display: favicon ? "none" : "block" }}
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    </div>

                    <input
                      className="form-input"
                      type="url"
                      value={url}
                      placeholder="https://..."
                      onChange={(e) => updateLink(i, e.target.value)}
                    />

                    <button
                      type="button"
                      className="remove-link-btn"
                      onClick={() => removeLink(i)}
                    >
                      <svg
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="add-link-btn"
              style={{ marginTop: 12 }}
              onClick={addLink}
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add social link
            </button>
          </section>

          {/* ── CREATOR TAGS ── */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon green">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">Creator Tags</h2>
                <p className="section-subtitle">
                  Help supporters find you on the Explore page
                </p>
              </div>
            </div>

            <p className="form-help" style={{ marginBottom: 16 }}>
              Select up to{" "}
              <strong style={{ color: "var(--text-primary)" }}>5 tags</strong>{" "}
              that best describe your content.
            </p>

            <div className="tags-grid">
              {ALL_TAGS.map((tag) => {
                const sel = selectedTags.has(tag.id);
                const disabled = !sel && selectedTags.size >= 5;

                return (
                  <label
                    key={tag.id}
                    className={`tag-chip${sel ? " selected" : ""}${disabled ? " disabled" : ""}`}
                    onClick={() => !disabled && toggleTag(tag.id)}
                  >
                    <span className="tag-icon">{tag.icon}</span>
                    <span className="tag-label">{tag.label}</span>
                    {sel && (
                      <svg
                        width="14"
                        height="14"
                        fill="none"
                        stroke="var(--primary)"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </label>
                );
              })}
            </div>

            <div className="tags-counter">
              <span className="tags-count">
                <span>{selectedTags.size}</span>/5 tags selected
              </span>
            </div>
          </section>

          <div style={{ height: 80 }} />
        </div>
      </div>

      <SaveBar
        visible={dirty}
        onDiscard={discardChanges}
        onSave={handleSave}
        saving={saving}
      />

      <ConfirmModal
        open={showDiscardModal}
        onClose={handleStayOnPage}
        onConfirm={handleLeavePage}
        title="You have unsaved changes"
        description="Your unsaved changes will be lost if you leave this page."
        confirmText="Leave page"
        cancelText="Stay here"
      />
    </>
  );
}
